import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  useColorScheme
} from '@mui/material';
import { useRouteError } from 'react-router';

import logoDark from '@/assets/pondurance-horizontal-full-black.png';
import logoLight from '@/assets/pondurance-horizontal-full-white.png';
import useRemoveAppSkeleton from '@/hooks/useRemoveAppSkeleton';

export const ErrorFallback = ({
  error,
  resetError
}: {
  error?: Error | { message: string };
  resetError?: () => void;
}) => {
  const { colorScheme } = useColorScheme();
  const routeError = useRouteError();
  const displayError = error || routeError;

  useRemoveAppSkeleton();

  const errorMessage =
    displayError instanceof Error
      ? displayError.message
      : (displayError as { message?: string })?.message ||
        'Unknown error occurred';

  const handleReset = () => {
    if (resetError) {
      resetError();
    } else {
      window.location.href = '/';
    }
  };

  return (
    <Container
      maxWidth="md"
      sx={{
        mt: 8,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}
    >
      <img
        src={colorScheme === 'light' ? logoDark : logoLight}
        alt="App Logo"
        height={100}
        style={{ margin: '2rem' }}
      />
      <Paper
        elevation={5}
        sx={{
          textAlign: 'center',
          p: 4
        }}
      >
        <Typography variant="h2" sx={{ mb: 2, color: 'error.main' }}>
          Oops! Something went wrong
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
          We&apos;re sorry for the inconvenience. An unexpected error has
          occurred. <br />
          Please try refreshing the page or going back to the home page.
        </Typography>

        {import.meta.env.DEV && errorMessage && (
          <Paper
            variant="outlined"
            elevation={1}
            sx={{
              mb: 2,
              p: 2,
              textAlign: 'left'
            }}
          >
            <Typography sx={{ fontWeight: 'bold' }} variant="caption">
              Error Details:
            </Typography>
            <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}>
              {errorMessage}
            </pre>
          </Paper>
        )}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', pt: 1 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => window.location.reload()}
          >
            Refresh Page
          </Button>
          <Button variant="contained" color="primary" onClick={handleReset}>
            Go to Home
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default ErrorFallback;
